'use client';

import { Form, Nimbus3Scope } from '@gartner-digital-markets/nimbus-v3-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { identifyVisitorByEmail } from '../browser/identity';
import { createGatewaySubmitAction } from '../browser/submission';
import type {
  ClientDataContextProps,
  FormPayload,
  FormTracking,
  OnServerLog,
  SubmitFormResult,
} from '../core';
import { ComparisonChartIcon } from './components/ComparisonChartIcon';
import { EmailField } from './components/EmailField';
import { type CardBackground, FormCard } from './components/FormCard';
import { FormFooter } from './components/FormFooter';
import { FormHeadline } from './components/FormHeadline';
import { SelectField } from './components/SelectField';
import { SubmissionErrorMessage } from './components/SubmissionErrorMessage';
import { SubmitButton } from './components/SubmitButton';
import { SuccessScreen } from './components/SuccessScreen';
import { TextField } from './components/TextField';
import { useEmailStorage } from './hooks/useEmailStorage';
import { useFormAnalytics } from './hooks/useFormAnalytics';
import { useFormSubmission } from './hooks/useFormSubmission';
import { useMultiStepForm } from './hooks/useMultiStepForm';

/**
 * Field configuration for MultiFieldInlineForm.
 */
export interface FieldConfig {
  name: string;
  type: 'email' | 'text' | 'select' | 'Page Separator';
  label?: string;
  placeholder?: string;
  options?: string[];
  required?: boolean;
  /** If true, field spans full width (both columns) in horizontal layout */
  fullWidth?: boolean;
}

// ============================================================================
// FIELD OPTIONS (reusable across presets)
// ============================================================================

/** Job function options used across Contentful forms */
export const JOB_FUNCTION_OPTIONS = [
  'Audit and Risk',
  'Accounting',
  'Coaching/Training',
  'Communications',
  'Customer Service and Support',
  'Education',
  'Finance',
  'Human Resources',
  'Information Technology',
  'Investment',
  'Legal and Compliance',
  'Marketing',
  'Procurement',
  'Project Management',
  'Research and Development',
  'Sales',
  'Software Provider',
  'Strategy Leader',
  'Supply Chain/Logistics',
];

/** Seniority options used across Contentful forms */
export const SENIORITY_OPTIONS = [
  'Administration',
  'Analyst',
  'Consultant',
  'Director',
  'Manager',
  'Owner/Founder',
  'Specialist/Coordinator',
  'Vice President',
];

// ============================================================================
// FIELD PRESETS - Single Step (all fields on one page)
// ============================================================================

/**
 * Preset: 2 fields, single step
 * Fields: firstName, emailAddress
 * Use for: Newsletter signups, simple lead capture
 */
export const SINGLE_STEP_2_FIELDS: FieldConfig[] = [
  { name: 'firstName', type: 'text', label: 'First Name' },
  { name: 'emailAddress', type: 'email', label: 'Email Address' },
];

/**
 * Preset: 3 fields, single step
 * Fields: firstName, emailAddress, jobFunction
 * Use for: Most Contentful "Inline: Card" forms (22 of 24)
 */
export const SINGLE_STEP_3_FIELDS: FieldConfig[] = [
  { name: 'firstName', type: 'text', label: 'First Name' },
  { name: 'emailAddress', type: 'email', label: 'Email Address' },
  {
    name: 'jobFunction',
    type: 'select',
    label: 'Job Function',
    options: JOB_FUNCTION_OPTIONS,
  },
];

/**
 * Preset: 4 fields, single step
 * Fields: emailAddress, firstName, jobFunction, seniority
 * Use for: Comprehensive lead capture without multi-step flow
 */
export const SINGLE_STEP_4_FIELDS: FieldConfig[] = [
  { name: 'emailAddress', type: 'email', label: 'Email Address' },
  { name: 'firstName', type: 'text', label: 'First Name' },
  {
    name: 'jobFunction',
    type: 'select',
    label: 'Job Function',
    options: JOB_FUNCTION_OPTIONS,
  },
  {
    name: 'seniority',
    type: 'select',
    label: 'Seniority',
    options: SENIORITY_OPTIONS,
  },
];

// ============================================================================
// FIELD PRESETS - Two Step (with Page Separator)
// ============================================================================

/**
 * Default two-step form fields configuration.
 * Step 1: emailAddress, firstName
 * Step 2: jobFunction, seniority
 */
export const TWO_STEP_DEFAULT_FIELDS: FieldConfig[] = [
  { name: 'emailAddress', type: 'email', label: 'Email Address' },
  { name: 'firstName', type: 'text', label: 'First Name' },
  { name: 'separator', type: 'Page Separator' },
  {
    name: 'jobFunction',
    type: 'select',
    label: 'Job Function',
    options: JOB_FUNCTION_OPTIONS,
  },
  {
    name: 'seniority',
    type: 'select',
    label: 'Seniority',
    options: SENIORITY_OPTIONS,
  },
];

/** @deprecated Use TWO_STEP_DEFAULT_FIELDS instead */
const DEFAULT_FORM_FIELDS = TWO_STEP_DEFAULT_FIELDS;

/**
 * Field layout options for the form.
 * - 'horizontal': Fields side-by-side on desktop (default for two-step)
 * - 'vertical': Fields stacked vertically (ideal for single-step newsletter forms)
 * - 'mixed': First two fields (firstName + email) side-by-side, rest stacked vertically
 */
export type FieldLayout = 'horizontal' | 'vertical' | 'mixed';

export interface MultiFieldInlineFormProps {
  formId: string;
  lang?: string;
  /** Custom fields configuration. If not provided, uses default two-step fields. */
  fields?: FieldConfig[];
  /** Field layout: 'horizontal' (side-by-side) or 'vertical' (stacked). Defaults to 'horizontal'. */
  fieldLayout?: FieldLayout;
  /** Nimbus theme: 'light' (default) or 'dark'. */
  theme?: 'light' | 'dark';
  headline?: string;
  subHeading?: string;
  buttonText?: string;
  step2Headline?: string;
  step2SubHeading?: string;
  secondButtonText?: string;
  successHeadline?: string;
  successMessage?: string;
  successIcon?: ReactNode;
  /** Optional success image rendered below the success message */
  successImage?: ReactNode;
  contextProps?: ClientDataContextProps;
  tracking?: FormTracking;
  gatewayUrl?: string;
  submitAction?: (payload: FormPayload) => Promise<SubmitFormResult>;
  onServerLog?: OnServerLog;
  /** Card background style: 'default' (white) or 'brand' (brand secondary color). */
  cardBackground?: CardBackground;
}

export interface MultiFieldInlineFormBrandConfig {
  defaultTracking?: FormTracking;
  successIcon?: ReactNode;
  /** Default success image for the brand. Rendered when successImage prop is not provided. */
  successImage?: ReactNode;
  termsUrl?: string;
  privacyUrl?: string;
  submissionFailedMessageKey?: string;
}

export interface SharedMultiFieldInlineFormProps
  extends MultiFieldInlineFormProps {
  brandConfig: MultiFieldInlineFormBrandConfig;
}

/**
 * Split fields into steps based on "Page Separator" field type.
 */
const splitFieldsIntoSteps = (
  fields: FieldConfig[]
): [FieldConfig[], FieldConfig[]] => {
  const separatorIndex = fields.findIndex((f) => f.type === 'Page Separator');

  if (separatorIndex === -1) {
    // No separator - all fields in step 1
    return [fields.filter((f) => f.type !== 'Page Separator'), []];
  }

  const step1Fields = fields
    .slice(0, separatorIndex)
    .filter((f) => f.type !== 'Page Separator');
  const step2Fields = fields
    .slice(separatorIndex + 1)
    .filter((f) => f.type !== 'Page Separator');

  return [step1Fields, step2Fields];
};

/**
 * Create a Zod schema from field configurations.
 */
const createSchemaFromFields = (fields: FieldConfig[]) => {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    if (field.type === 'Page Separator') continue;

    const isRequired = field.required !== false;

    if (field.type === 'email') {
      schemaShape[field.name] = z
        .string()
        .min(1, 'Email is required')
        .email('Please enter a valid email address');
    } else if (field.type === 'select') {
      schemaShape[field.name] = isRequired
        ? z.string().min(1, 'Please select an option')
        : z.string().optional();
    } else {
      schemaShape[field.name] = isRequired
        ? z.string().min(1, `${field.label || field.name} is required`)
        : z.string().optional();
    }
  }

  return z.object(schemaShape);
};

/**
 * Create default values from field configurations.
 */
const createDefaultValuesFromFields = (
  fields: FieldConfig[],
  storedEmail?: string
): Record<string, string> => {
  const defaults: Record<string, string> = {};

  for (const field of fields) {
    if (field.type === 'Page Separator') continue;

    if (field.type === 'email' && storedEmail) {
      defaults[field.name] = storedEmail;
    } else {
      defaults[field.name] = '';
    }
  }

  return defaults;
};

/**
 * Render a field based on its configuration.
 */
const renderField = (
  field: FieldConfig,
  analytics: ReturnType<typeof useFormAnalytics>
) => {
  switch (field.type) {
    case 'email':
      return (
        <EmailField
          key={field.name}
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required !== false}
          onPointerDown={() => analytics.trackFormStarted('email-box-click')}
          onInput={() => analytics.trackFormStarted('email-box-type')}
        />
      );
    case 'text':
      return (
        <TextField
          key={field.name}
          name={field.name}
          label={field.label || field.name}
          placeholder={field.placeholder}
          required={field.required !== false}
        />
      );
    case 'select':
      return (
        <SelectField
          key={field.name}
          name={field.name}
          label={field.label || field.name}
          options={field.options || []}
          placeholder={field.placeholder}
          required={field.required !== false}
        />
      );
    default:
      return null;
  }
};

/**
 * Renders a multi-field inline form with configurable fields and optional two-step flow.
 *
 * This component displays an embedded form with customizable field configurations
 * supporting text, email, and select inputs. Fields can be split across two steps
 * using a "Page Separator" field type.
 *
 * @example
 * ```tsx
 * import { MultiFieldInlineForm, SINGLE_STEP_3_FIELDS } from 'bx-forms/react';
 *
 * // Using preset fields
 * <MultiFieldInlineForm
 *   formId="contentful-form-id"
 *   fields={SINGLE_STEP_3_FIELDS}
 *   headline="Download the report"
 * />
 *
 * // Custom two-step fields
 * <MultiFieldInlineForm
 *   formId="contentful-form-id"
 *   fields={[
 *     { name: 'email', type: 'email', label: 'Email' },
 *     { name: 'separator', type: 'Page Separator' },
 *     { name: 'company', type: 'text', label: 'Company' },
 *   ]}
 * />
 * ```
 */
export const MultiFieldInlineForm = ({
  formId,
  lang,
  fields,
  fieldLayout = 'horizontal',
  theme = 'light',
  headline = "Capterra's 2026 Software Buying Trends Report",
  subHeading = 'Download our 2026 Software Buying Trends Report to see how successful software adopters avoid disappointment and how your business can, too.',
  buttonText = 'GET THE REPORT',
  step2Headline,
  step2SubHeading = 'Thank you! Just two more questions:',
  secondButtonText = 'SUBMIT',
  successHeadline = 'Thank you!',
  successMessage = 'Your report will be delivered to your inbox shortly.',
  successIcon,
  successImage,
  contextProps,
  tracking,
  gatewayUrl = '/bx-capterra-api/bx-forms/submit',
  submitAction,
  onServerLog,
  cardBackground,
  brandConfig,
}: SharedMultiFieldInlineFormProps) => {
  // Use custom fields if provided, otherwise fall back to default two-step fields
  const formFields = fields ?? DEFAULT_FORM_FIELDS;

  const [step1Fields, step2Fields] = useMemo(
    () => splitFieldsIntoSteps(formFields),
    [formFields]
  );
  const isTwoStep = step2Fields.length > 0;
  const totalSteps = isTwoStep ? 3 : 2; // step1 + (step2?) + success

  const mergedTracking = useMemo(
    () => ({ ...(brandConfig.defaultTracking ?? {}), ...tracking }),
    [brandConfig.defaultTracking, tracking]
  );

  const portalBridgeAttributes = useMemo(() => {
    const brand = brandConfig.defaultTracking?.brand;
    if (!brand) return undefined;
    return { 'data-bx-forms-brand': brand };
  }, [brandConfig.defaultTracking?.brand]);

  const resolvedSubmitAction = useMemo(
    () => submitAction ?? createGatewaySubmitAction(gatewayUrl),
    [submitAction, gatewayUrl]
  );

  const step1Schema = useMemo(
    () => createSchemaFromFields(step1Fields),
    [step1Fields]
  );
  const step2Schema = useMemo(
    () => createSchemaFromFields(step2Fields),
    [step2Fields]
  );

  const { step, formData, nextStep } = useMultiStepForm({ totalSteps });
  const { submitToApi, submissionErrorMessage, isLoading } = useFormSubmission({
    formId,
    tracking: mergedTracking,
    contextProps,
    templateName: 'multi-field-inline',
    submitAction: resolvedSubmitAction,
  });
  const analytics = useFormAnalytics(formId, 'inline');
  const { setStoredEmail, getStoredEmail } = useEmailStorage();
  const formRef = useRef<HTMLDivElement>(null);

  const step1DefaultValues = useMemo(
    () => createDefaultValuesFromFields(step1Fields, getStoredEmail() || ''),
    [step1Fields, getStoredEmail]
  );
  const step2DefaultValues = useMemo(
    () => createDefaultValuesFromFields(step2Fields),
    [step2Fields]
  );

  const step1Form = useForm({
    resolver: zodResolver(step1Schema),
    defaultValues: step1DefaultValues,
    mode: 'onBlur',
  });

  const step2Form = useForm({
    resolver: zodResolver(step2Schema),
    defaultValues: step2DefaultValues,
    mode: 'onBlur',
  });

  // Analytics: form failed to display
  useEffect(() => {
    if (!formId) {
      analytics.trackFormFailedToDisplay();
    }
  }, [formId, analytics]);

  // Analytics: form open and prefill tracking
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const isPrefilled = !!getStoredEmail();
            analytics.trackFormOpen();
            analytics.trackFormExposeServer({
              formTemplate: 'multi-field-inline',
              formKind: 'multi-field-inline',
              brand: mergedTracking.brand,
              placement: contextProps?.pagePlacement,
              page_type: contextProps?.pageType,
              page_path: window.location.pathname,
              eloqua_campaign: mergedTracking.eloquaCampaign,
            });
            if (isPrefilled) {
              analytics.trackFormPrefilled();
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    if (formRef.current) {
      observer.observe(formRef.current);
    }

    return () => observer.disconnect();
  }, [
    analytics,
    contextProps,
    getStoredEmail,
    mergedTracking.brand,
    mergedTracking.eloquaCampaign,
  ]);

  // Analytics: step 2 displayed
  useEffect(() => {
    if (isTwoStep && step === 1) {
      analytics.trackMicroDataQuestionDisplayed();
    }
  }, [analytics, isTwoStep, step]);

  const handleSubmissionFailure = (result: SubmitFormResult) => {
    onServerLog?.(
      JSON.stringify({
        messageKey:
          brandConfig.submissionFailedMessageKey ??
          'bx_forms_submission_failed',
        message: `Form submission failed: ${JSON.stringify(result)}`,
        formId,
        contextProps,
      })
    );
  };

  const handleStep1Submit = async (data: Record<string, string>) => {
    // Store email if present
    const emailField = step1Fields.find((f) => f.type === 'email');
    const submittedEmail = emailField ? data[emailField.name] : undefined;
    if (submittedEmail) {
      setStoredEmail(submittedEmail);
    }

    if (isTwoStep) {
      // Two-step: submit step 1 data, move to step 2
      const result = await submitToApi({
        ...formData,
        ...data,
        hasSecondPage: true,
      });

      if (result.success) {
        if (submittedEmail) {
          identifyVisitorByEmail(submittedEmail);
        }
        analytics.trackFormSubmitted(buttonText);
        nextStep(data);
        return;
      }

      handleSubmissionFailure(result);
    } else {
      // Single step: submit and go to success
      const result = await submitToApi({
        ...formData,
        ...data,
        hasSecondPage: false,
      });

      if (result.success) {
        if (submittedEmail) {
          identifyVisitorByEmail(submittedEmail);
        }
        analytics.trackFormSubmitted(buttonText);
        nextStep(data);
        return;
      }

      handleSubmissionFailure(result);
    }
  };

  const handleStep2Submit = async (data: Record<string, string>) => {
    const result = await submitToApi({
      ...formData,
      ...data,
      hasSecondPage: false,
    });

    if (result.success) {
      analytics.trackMicroDataQuestionSubmitted();
      nextStep(data);
      return;
    }

    handleSubmissionFailure(result);
  };

  const handleError = (currentStep: number) => {
    if (currentStep === 0) {
      analytics.trackFormFailed();
    } else {
      analytics.trackMicroDataQuestionFailed();
    }
  };

  const successStep = isTwoStep ? 2 : 1;

  // Resolve success icon: prop → brand config → default (null hides it)
  const defaultSuccessIcon = <ComparisonChartIcon />;
  const resolvedSuccessIcon =
    successIcon !== undefined
      ? successIcon
      : (brandConfig.successIcon ?? defaultSuccessIcon);

  // Resolve success image: prop → brand config → null (only shown when explicitly provided)
  const resolvedSuccessImage =
    successImage !== undefined
      ? successImage
      : (brandConfig.successImage ?? null);

  // Success screen
  if (step === successStep) {
    return (
      <Nimbus3Scope
        theme={theme}
        portalBridgeAttributes={portalBridgeAttributes}
        style={{ background: 'transparent' }}
      >
        <FormCard className="gap-0 p-6" cardBackground={cardBackground}>
          <SuccessScreen
            headline={successHeadline}
            message={successMessage}
            icon={resolvedSuccessIcon}
          />
          {resolvedSuccessImage && (
            <div className="mt-6 flex justify-center">
              {resolvedSuccessImage}
            </div>
          )}
        </FormCard>
      </Nimbus3Scope>
    );
  }

  return (
    <Nimbus3Scope
      theme={theme}
      portalBridgeAttributes={portalBridgeAttributes}
      style={{ background: 'transparent' }}
    >
      {/* biome-ignore lint/nursery/noStaticElementInteractions: Analytics-only tracking */}
      <div
        ref={formRef}
        onMouseEnter={() => analytics.trackFormMouseover()}
        role="presentation"
      >
        <FormCard className="gap-0 p-6" cardBackground={cardBackground}>
          {/* Step 1 */}
          {step === 0 && (
            <>
              <FormHeadline title={headline} subtitle={subHeading} />
              <Form {...step1Form}>
                <form
                  onSubmit={step1Form.handleSubmit(handleStep1Submit, () =>
                    handleError(0)
                  )}
                  className="space-y-6"
                >
                  {/* Field layout: horizontal, vertical, or mixed */}
                  {fieldLayout === 'mixed' ? (
                    <div className="flex flex-col gap-4">
                      {/* First two fields side-by-side */}
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {step1Fields.slice(0, 2).map((field) => (
                          <div key={field.name}>
                            {renderField(field, analytics)}
                          </div>
                        ))}
                      </div>
                      {/* Remaining fields stacked vertically */}
                      {step1Fields.slice(2).map((field) => (
                        <div key={field.name}>
                          {renderField(field, analytics)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className={
                        fieldLayout === 'vertical'
                          ? 'flex flex-col gap-4'
                          : 'grid grid-cols-1 gap-4 md:grid-cols-2'
                      }
                    >
                      {step1Fields.map((field) => (
                        <div
                          key={field.name}
                          className={field.fullWidth ? 'md:col-span-2' : ''}
                        >
                          {renderField(field, analytics)}
                        </div>
                      ))}
                    </div>
                  )}
                  <FormFooter
                    lang={lang}
                    termsUrl={brandConfig.termsUrl}
                    privacyUrl={brandConfig.privacyUrl}
                  />
                  <div className="flex justify-end pt-2">
                    <SubmitButton
                      text={buttonText}
                      isLoading={isLoading}
                      className="w-full md:w-auto"
                    />
                  </div>
                </form>
              </Form>
            </>
          )}

          {/* Step 2 (if two-step form) */}
          {isTwoStep && step === 1 && (
            <>
              <FormHeadline
                title={step2Headline || headline}
                subtitle={step2SubHeading}
                showRuler={true}
              />
              <Form {...step2Form}>
                <form
                  onSubmit={step2Form.handleSubmit(handleStep2Submit, () =>
                    handleError(1)
                  )}
                  className="space-y-6"
                >
                  {/* Field layout: horizontal, vertical, or mixed */}
                  {fieldLayout === 'mixed' ? (
                    <div className="flex flex-col gap-4">
                      {/* First two fields side-by-side */}
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {step2Fields.slice(0, 2).map((field) => (
                          <div key={field.name}>
                            {renderField(field, analytics)}
                          </div>
                        ))}
                      </div>
                      {/* Remaining fields stacked vertically */}
                      {step2Fields.slice(2).map((field) => (
                        <div key={field.name}>
                          {renderField(field, analytics)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className={
                        fieldLayout === 'vertical'
                          ? 'flex flex-col gap-4'
                          : 'grid grid-cols-1 gap-4 md:grid-cols-2'
                      }
                    >
                      {step2Fields.map((field) => (
                        <div
                          key={field.name}
                          className={field.fullWidth ? 'md:col-span-2' : ''}
                        >
                          {renderField(field, analytics)}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-end pt-2">
                    <SubmitButton
                      text={secondButtonText}
                      isLoading={isLoading}
                      className="w-full md:w-auto"
                    />
                  </div>
                </form>
              </Form>
            </>
          )}
          <SubmissionErrorMessage message={submissionErrorMessage} />
        </FormCard>
      </div>
    </Nimbus3Scope>
  );
};
